import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talents, accounts } from '../services/apiService';
import TalentForm from '../components/UI/TalentForm';
import { Loader } from '../components/UI/LoadingSpinner';
import AccountTab from '../components/Talent/AccountTab';
import MyAccountsTab from '../components/Talent/MyAccountsTab';
import AssignedSocialAccountsTab from '../components/Talent/AssignedSocialAccountsTab';
import AssignedCredentialsTab from '../components/Talent/AssignedCredentialsTab';
import { useAuth } from '../hooks/useAuth';
import { User, AlertCircle, ArrowLeft, CreditCard, UserCircle, Users, KeyRound } from 'lucide-react';

// Component for displaying accounts using /self endpoint
const AccountsTabContent = ({ userId }) => {
  const [accountsList, setAccountsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchAccountData();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await accounts.getUserSelfAccounts(userId);

      if (response.success && response.data) {
        // response.data теперь массив аккаунтов
        setAccountsList(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.error || 'Failed to fetch account data');
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
      setError('Error loading account data');
    } finally {
      setLoading(false);
    }
  };

  // Reuse AccountTab component for rendering with external data
  return (
    <AccountTab
      userId={userId}
      accountsList={accountsList}
      loading={loading}
      error={error}
      onRefresh={fetchAccountData}
    />
  );
};

// Component for displaying social accounts using /self endpoint
const AssignedSocialAccountsTabContent = ({ userId, talentType }) => {
  const [socialAccountsList, setSocialAccountsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchSocialAccountsData();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchSocialAccountsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await accounts.getSocialAccountsByAssignedUserIdSelf(userId);

      if (response.success && response.data) {
        setSocialAccountsList(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.error || 'Failed to fetch social accounts data');
      }
    } catch (err) {
      console.error('Error fetching social accounts data:', err);
      setError('Error loading social accounts data');
    } finally {
      setLoading(false);
    }
  };

  // Reuse AssignedSocialAccountsTab component for rendering with external data
  return (
    <AssignedSocialAccountsTab
      userId={userId}
      talentType={talentType}
      socialAccountsList={socialAccountsList}
      loading={loading}
      error={error}
      selfMode={true}
    />
  );
};

// Component for displaying account credentials using /self endpoint
const AssignedCredentialsTabContent = ({ userId }) => {
  const [credentialsList, setCredentialsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchCredentialsData();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchCredentialsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await accounts.getAssignedCredentialsSelf(userId);

      if (response.success && response.data) {
        setCredentialsList(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.error || 'Failed to fetch credentials data');
      }
    } catch (err) {
      console.error('Error fetching credentials data:', err);
      setError('Error loading credentials data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AssignedCredentialsTab
      userId={userId}
      credentialsList={credentialsList}
      loading={loading}
      error={error}
    />
  );
};

const MyProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // Get current user profile
  const {
    data: profileResponse,
    isLoading,
    isError,
    error: fetchError
  } = useQuery({
    queryKey: ['myProfile'],
    queryFn: talents.getMyProfile,
    retry: false
  });

  const profile = profileResponse?.data;

  // Mutation for profile update
  const updateMutation = useMutation({
    mutationFn: (data) => talents.updateMyProfile(data),
    onSuccess: (response) => {
      console.log('✅ Profile updated successfully:', response);

      // Invalidate profile cache
      queryClient.invalidateQueries(['myProfile']);

      // Clear error state
      setError(null);

      // Show success toast notification
      toast.success('Profile updated successfully!', {
        duration: 3000,
        position: 'top-right',
      });
    },
    onError: (error) => {
      console.error('❌ Error updating profile:', error);

      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          'Failed to update profile';

      setError(errorMessage);

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const handleSubmit = async (formData) => {
    console.log('🚀 Submitting profile update:', formData);
    setError(null);

    try {
      await updateMutation.mutateAsync(formData);
    } catch (err) {
      // Error already handled in onError — re-throw so TalentForm shows error instead of success
      console.error('Submit error:', err);
      throw err;
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  // Loading state
  if (isLoading) {
    return <Loader variant="component" size="md" />;
  }

  // Error handling for profile loading
  if (isError) {
    const errorMessage = fetchError?.response?.data?.message ||
                        fetchError?.response?.data?.error ||
                        'Failed to load profile';

    return (
      <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
        <div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                  Profile Loading Error
                </h3>
                <p className="text-red-700 dark:text-red-300">
                  {errorMessage}
                </p>
                {fetchError?.response?.status === 404 && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    You don't have a talent profile yet. Please contact an administrator to create your profile.
                  </p>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Home</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If profile not found
  if (!profile) {
    return (
      <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
        <div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                  Profile Not Found
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300">
                  You don't have a talent profile yet. Please contact an administrator to create your profile.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
      <title>My Profile | Talent Service</title>
      <div>
        {/* Header */}
        <div className="mb-6">
          <div className="mb-2 flex min-w-0 items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="min-w-0 text-2xl font-bold text-light-text-primary dark:text-dark-text-primary sm:text-3xl">
              My Profile
            </h1>
          </div>
          <p className="text-light-text-secondary dark:text-dark-text-secondary">
            Edit your information. Main profession cannot be changed.
          </p>
        </div>

        {/* Show error if exists */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                  Profile Update Error
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-light-bg-primary dark:bg-dark-bg-secondary shadow rounded-lg border border-light-border-primary dark:border-dark-border-primary mb-6">
          {/* Tabs navigation */}
          <div className="border-b border-light-border-primary dark:border-dark-border-primary">
            <nav className="-mb-px flex min-w-0 snap-x snap-proximity flex-nowrap gap-1 overflow-x-auto px-4 scrollbar-hide sm:px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex flex-shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium ${
                  activeTab === 'profile'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Profile</span>
              </button>
              <button
                onClick={() => setActiveTab('accounts')}
                className={`flex flex-shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium ${
                  activeTab === 'accounts'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                <span>Assigned Accounts</span>
              </button>
              <button
                onClick={() => setActiveTab('assignedSocialAccounts')}
                className={`flex flex-shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium ${
                  activeTab === 'assignedSocialAccounts'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
              >
                <Users className="h-4 w-4" />
                <span>Assigned Social Accounts</span>
              </button>
              <button
                onClick={() => setActiveTab('myAccounts')}
                className={`flex flex-shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium ${
                  activeTab === 'myAccounts'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
              >
                <UserCircle className="h-4 w-4" />
                <span>My Accounts</span>
              </button>
              <button
                onClick={() => setActiveTab('assignedCredentials')}
                className={`flex flex-shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium ${
                  activeTab === 'assignedCredentials'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
                }`}
              >
                <KeyRound className="h-4 w-4" />
                <span>Assigned Account Credentials</span>
              </button>
            </nav>
          </div>

          {/* Tab content */}
          <div className="min-h-[300px] min-w-0 p-4 sm:p-6">
            {activeTab === 'profile' && (
              <TalentForm
                initialData={profile}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                title="Edit Profile"
                submitText="Save Changes"
                showHeader={false}
                isModal={false}
                talentId={profile.id}
                isMyProfile={true}
              />
            )}
            {activeTab === 'accounts' && user?.id && (
              <AccountsTabContent userId={user.id} />
            )}
            {activeTab === 'accounts' && !user?.id && (
              <div className="text-center py-12">
                <User className="mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary mb-4" />
                <p className="text-light-text-secondary dark:text-dark-text-secondary text-lg mb-2">User not found</p>
                <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Unable to load account information</p>
              </div>
            )}
            {activeTab === 'myAccounts' && user?.id && (
              <MyAccountsTab userId={user.id} />
            )}
            {activeTab === 'assignedSocialAccounts' && user?.id && profile?.talent_type && (
              <AssignedSocialAccountsTabContent userId={user.id} talentType={profile.talent_type} />
            )}
            {activeTab === 'assignedSocialAccounts' && (!user?.id || !profile?.talent_type) && (
              <div className="text-center py-12">
                <Users className="mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary mb-4" />
                <p className="text-light-text-secondary dark:text-dark-text-secondary text-lg mb-2">Unable to load social accounts</p>
                <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">User or profile type not found</p>
              </div>
            )}
            {activeTab === 'assignedCredentials' && user?.id && (
              <AssignedCredentialsTabContent userId={user.id} />
            )}
            {activeTab === 'assignedCredentials' && !user?.id && (
              <div className="text-center py-12">
                <KeyRound className="mx-auto h-16 w-16 text-light-text-tertiary dark:text-dark-text-tertiary mb-4" />
                <p className="text-light-text-secondary dark:text-dark-text-secondary text-lg mb-2">User not found</p>
                <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Unable to load credentials information</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
